import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { setCNVsCodingGenes } from "../helpers/Setters";
import CnvResultsTable from "./CnvResultsTable";
import SingleLineCnvResults from "./SingleLineCnvResults";
import { sortAlphaNumericArr } from "../helpers/helpers";
import useTextExpander from "../hooks/useTextExpander";
import HoverTooltip from "../../../components/atoms/Tooltip/HoverTooltip";
import ComponentPanel from "../../../components/atoms/ComponentPanel/ComponentPanel";
import HelperTextLabel from "../../../components/atoms/HelperText/HelperTextLabel";
import { SV_BREAKEND } from "../../../common/constants";
import ClickTooltip from "../../../components/atoms/Tooltip/ClickTooltip";
import QuestionCircleIcon from "../../../components/icons/QuestionCircle";

const CnvDetails = ({ variantResult, hostUrl, pathogenicGenes }) => {
  const [state, setState] = useState({
    codingGenes: [],
    nonCodingGenes: [],
  });
  const [expandedText, handleExpansion] = useTextExpander();
  const isBreakend = variantResult.type === SV_BREAKEND;

  useEffect(() => {
    if (
      // Coding genes are inside coding_genes and ALL genes are included in gene_symbol. Ignore the genes array it is not stable
      !variantResult ||
      !variantResult.gene_symbol
    ) {
      setState({ ...state, codingGenes: [], nonCodingGenes: [] });
      setCNVsCodingGenes([]);
      return;
    }
    if (!variantResult.coding_genes && !variantResult?.gene_symbol) {
      setState({ ...state, codingGenes: [] });
      setCNVsCodingGenes([]);
      return;
    }
    const newState = { ...state };
    newState.codingGenes = variantResult?.coding_genes || [];
    newState.nonCodingGenes = variantResult?.gene_symbol || [];
    const filteredResults = newState.nonCodingGenes.filter(
      (val) => !newState.codingGenes.includes(val)
    );
    newState.nonCodingGenes = filteredResults.length
      ? sortAlphaNumericArr(filteredResults)
      : [];
    setState(newState);
    // Get only the first 15 genes out and put them in the sidebar
    if (newState.codingGenes?.length) {
      setCNVsCodingGenes(sortAlphaNumericArr(newState.codingGenes));
    }
  }, [variantResult]);

  const altSequence = variantResult?.breakend_info
    ? variantResult.breakend_info?.inserted_sequence
    : variantResult?.alt;

  const altSequenceTrunc =
    altSequence?.length > 50
      ? `${altSequence.slice(0, 2)}...${altSequence.slice(-2)}`
      : null;

  return (
    <div className="varsome-result">
      <div className="varsome-container varsome-container__basic">
        <ComponentPanel
          componentName="SV Details Component"
          title="SV Details"
          content={
            <div className="varsome-inner-container">
              <div className="varsome-horiz-flex">
                {isBreakend ? (
                  <div className="varsome-horiz-list-item">
                    <div className="varsome-horiz-list-label">Chromosome A</div>
                    <div className="varsome-horiz-list-value">
                      {variantResult?.chromosome || "Not found"}
                    </div>
                    <div className="varsome-horiz-list-label">Chromosome B</div>
                    <div className="varsome-horiz-list-value">
                      {variantResult?.breakend_info?.connected_chromosome ||
                        "Not found"}
                    </div>
                  </div>
                ) : (
                  <div className="varsome-horiz-list-item">
                    <div className="varsome-horiz-list-label">Chromosome</div>
                    <div className="varsome-horiz-list-value">
                      {variantResult?.chromosome || "Not found"}
                    </div>
                  </div>
                )}
                {isBreakend ? (
                  <div className="varsome-horiz-list-item">
                    <div className="varsome-horiz-list-label">Position A</div>
                    <div className="varsome-horiz-list-value">
                      {variantResult?.pos || "Not found"}
                    </div>
                    <div className="varsome-horiz-list-label">Position B</div>
                    <div className="varsome-horiz-list-value">
                      {variantResult?.breakend_info?.connected_position ||
                        "Not found"}
                    </div>
                  </div>
                ) : (
                  <div className="varsome-horiz-list-item">
                    <div className="varsome-horiz-list-label">Position</div>
                    <div className="varsome-horiz-list-value">
                      {variantResult?.pos || "Not found"}
                    </div>
                  </div>
                )}

                <div className="varsome-horiz-list-item">
                  <div className="varsome-horiz-list-label">End</div>
                  <div className="varsome-horiz-list-value">
                    {variantResult?.end || "Not found"}
                  </div>
                </div>
                <div className="varsome-horiz-list-item">
                  <div className="varsome-horiz-list-label">Type</div>
                  <div className="varsome-horiz-list-value">
                    {variantResult?.type || "Not found"}
                  </div>
                </div>
                <div className="varsome-horiz-list-item">
                  <HelperTextLabel
                    label="Cytoband"
                    fieldDescriptionKey="Cytoband"
                  />
                  <div className="varsome-horiz-list-value">
                    {variantResult?.cytobands?.length > 15 ? (
                      <HoverTooltip
                        ContentRenderer={() => {
                          return expandedText ? (
                            <div>Show less</div>
                          ) : (
                            <div>Show more</div>
                          );
                        }}
                      >
                        <a
                          onClick={(e) => {
                            e.preventDefault();
                            handleExpansion(!expandedText);
                          }}
                        >
                          {expandedText
                            ? variantResult?.cytobands || "Not found"
                            : `${variantResult?.cytobands?.substring(
                                0,
                                15
                              )}...` || "Not found"}
                        </a>
                      </HoverTooltip>
                    ) : (
                      variantResult?.cytobands || "Not found"
                    )}
                  </div>
                </div>
                <div className="varsome-horiz-list-item">
                  <div className="varsome-horiz-list-label">Length</div>
                  <div className="varsome-horiz-list-value">
                    {variantResult?.length || "Not found"}
                  </div>
                </div>
              </div>

              {altSequence || variantResult?.breakend_info?.breakend_type ? (
                <>
                  {variantResult.breakend_info ? (
                    <div className="varsome-horiz-flex">
                      <div className="varsome-horiz-list-item">
                        <div className="varsome-horiz-list-label">
                          Breakend Type
                          <ClickTooltip
                            className="genome-browser-tooltip"
                            zIndex={9999}
                            ContentRenderer={() => (
                              <div>
                                <div className="tw-text-base tw-font-semibold">
                                  Breakend type meaning
                                </div>
                                <table className="tw-table-auto tw-my-4">
                                  <thead>
                                    <tr className="tw-border-b tw-border-blueGray-200 tw-mb-2">
                                      <th className="tw-pr-4">Breakend Type</th>
                                      <th className="tw-pr-4">
                                        Direction of joined sequence
                                      </th>
                                      <th className="tw-pr-4">
                                        Sequence joining position
                                      </th>
                                      <th className="tw-pr-4">Meaning</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr className="tw-border-b tw-border-blueGray-200 tw-mb-2">
                                      <td className="tw-pr-4">t[p[</td>
                                      <td className="tw-pr-4">
                                        Rightward (extending right of p)
                                      </td>
                                      <td className="tw-pr-4">After t</td>
                                      <td className="tw-pr-4">
                                        A piece extending to the right of p is
                                        <br />
                                        joined after the sequence t.
                                      </td>
                                    </tr>
                                    <tr className="tw-border-b tw-border-blueGray-200 tw-mb-2">
                                      <td className="tw-pr-4">t]p]</td>
                                      <td className="tw-pr-4">
                                        Leftward (extending left of p)
                                      </td>
                                      <td className="tw-pr-4">After t</td>
                                      <td className="tw-pr-4">
                                        A reverse complement piece extending
                                        left
                                        <br />
                                        of p is joined after the sequence t.
                                      </td>
                                    </tr>
                                    <tr className="tw-border-b tw-border-blueGray-200 tw-mb-2">
                                      <td className="tw-pr-4">]p]t</td>
                                      <td className="tw-pr-4">
                                        Leftward (extending left of p)
                                      </td>
                                      <td className="tw-pr-4">Before t</td>
                                      <td className="tw-pr-4">
                                        A piece extending to the left of p is
                                        <br />
                                        joined before the sequence t.
                                      </td>
                                    </tr>
                                    <tr className="tw-border-b tw-border-blueGray-200 tw-mb-2">
                                      <td className="tw-pr-4">[p[t</td>
                                      <td className="tw-pr-4">
                                        Rightward (extending right of p)
                                      </td>
                                      <td className="tw-pr-4">Before t</td>
                                      <td className="tw-pr-4">
                                        A reverse complement piece extending
                                        right
                                        <br />
                                        of p is joined before the sequence t.
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                                <div className="tw-italic tw-my-4">
                                  <b>t</b>: The replacement sequence that
                                  substitutes the reference sequence at the
                                  breakend.
                                  <br />
                                  <b>p</b>: The genomic position (in the form of
                                  chr:pos) where the mate breakend is located,
                                  indicating where the joined sequence starts.
                                </div>
                              </div>
                            )}
                          >
                            <div className="tw-inline tw-cursor-pointer">
                              <QuestionCircleIcon
                                size={12}
                                className="tw-ml-1 tw-fill-current tw-text-sky-300 tw-opacity-60 hover:tw-opacity-100 tw-cursor-pointer"
                              />
                            </div>
                          </ClickTooltip>
                        </div>
                        <div className="varsome-horiz-list-value tw-lowercase">
                          {variantResult.breakend_info?.breakend_type}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  {altSequence ? (
                    <div className="varsome-horiz-flex tw-max-w-full">
                      <div className="varsome-horiz-list-item">
                        <div className="varsome-horiz-list-label">
                          ALT sequence
                        </div>
                        <div className="tw-flex tw-items-center tw-justify-center tw-w-full">
                          {altSequenceTrunc ? (
                            <ClickTooltip
                              className="genome-browser-tooltip"
                              zIndex={9999}
                              ContentRenderer={() => (
                                <div className="tw-break-all tw-max-w-72 tw-max-h-44 tw-overflow-auto">
                                  {altSequence.length} bp: {altSequence}
                                </div>
                              )}
                            >
                              <div
                                data-tooltip="click to view whole sequence"
                                className="tw-uppercase tw-truncate tw-underline tw-min-w-0 tw-max-w-60 tw-text-center tw-cursor-pointer"
                              >
                                {altSequenceTrunc} ({altSequence.length}{" "}
                                <span className="tw-lowercase">bp</span>)
                              </div>
                            </ClickTooltip>
                          ) : (
                            <div className="tw-uppercase tw-break-all tw-min-w-0 tw-text-center">
                              {altSequence}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </>
              ) : null}

              <div className="varsome-horiz-flex">
                <div className="varsome-horiz-list-item">
                  <div className="varsome-horiz-list-label">
                    Coding Genes{" "}
                    {state.codingGenes && (
                      <p className="tw-font-bold tw-text-base">
                        {state.codingGenes.length}
                      </p>
                    )}
                  </div>
                  <div className="varsome-horiz-list-value">
                    {state.codingGenes?.length > 6 ? (
                      <CnvResultsTable
                        values={state.codingGenes}
                        hostUrl={hostUrl}
                        isDefaultExpanded={true}
                        pathogenicGenes={pathogenicGenes}
                      />
                    ) : (
                      <SingleLineCnvResults
                        values={state.codingGenes}
                        hostUrl={hostUrl}
                        pathogenicGenes={pathogenicGenes}
                      />
                    )}
                  </div>
                </div>
              </div>
              <div className="varsome-horiz-flex">
                <div className="varsome-horiz-list-item">
                  <div className="varsome-horiz-list-label">
                    Non-Coding Genes{" "}
                    {state.nonCodingGenes && (
                      <p className="tw-font-bold tw-text-base">
                        {state.nonCodingGenes.length}
                      </p>
                    )}
                  </div>
                  <div className="varsome-horiz-list-value">
                    {state.nonCodingGenes?.length > 6 ? (
                      <CnvResultsTable
                        values={state.nonCodingGenes}
                        hostUrl={hostUrl}
                        pathogenicGenes={pathogenicGenes}
                      />
                    ) : (
                      <SingleLineCnvResults
                        values={state.nonCodingGenes}
                        hostUrl={hostUrl}
                        pathogenicGenes={pathogenicGenes}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          }
        />
      </div>
    </div>
  );
};

CnvDetails.propTypes = {
  variantResult: PropTypes.shape({
    alt: PropTypes.string,
    coding_gene_id: PropTypes.arrayOf(PropTypes.number),
    coding_genes: PropTypes.arrayOf(PropTypes.string),
    deleted_data_reason: PropTypes.string,
    exon: PropTypes.arrayOf(PropTypes.string),
    gene_id: PropTypes.arrayOf(PropTypes.number),
    gene_symbol: PropTypes.arrayOf(PropTypes.string),
    end: PropTypes.number,
    length: PropTypes.number,
    pos: PropTypes.number,
    cytobands: PropTypes.string,
    type: PropTypes.string,
    chromosome: PropTypes.string,
    breakend_info: PropTypes.shape({
      breakend_type: PropTypes.string,
      connected_chromosome: PropTypes.string,
      connected_position: PropTypes.number,
      inserted_sequence: PropTypes.string,
    }),
  }),
  hostUrl: PropTypes.string,
  pathogenicGenes: PropTypes.arrayOf(
    PropTypes.shape({
      disease: PropTypes.arrayOf(PropTypes.string),
      gene: PropTypes.string,
    })
  ),
};

CnvDetails.defaultProps = {
  variantResult: null,
  hostUrl: "",
  pathogenicGenes: null,
};

export default CnvDetails;
